import { useAppConfigContext } from 'contexts/AppConfigContext';
import { AppRouteContextProvider } from 'contexts/AppRouteContext';
import { Callback } from 'pages/Callback';
import { ErrorPage } from 'pages/Error';
import { ExternalPlaceholder } from 'pages/ExternalPlaceholder/ExternalPlaceholder';
import { FeatureFlagDefinition } from 'pages/FeatureFlag';
import { FeatureFlagDefinitionInformation } from 'pages/FeatureFlag/FeatureFlagDefinitionInformation';
import { FeatureFlagRegions } from 'pages/FeatureFlag/FeatureFlagRegions/FeatureFlagRegions';
import { FeatureFlagTenants } from 'pages/FeatureFlag/FeatureFlagTenants';
import { FeatureFlagList } from 'pages/FeatureFlagList';
import { GroupList } from 'pages/GroupList';
import { Home } from 'pages/Home';
import { LicenseList } from 'pages/LicenseList';
import { SignIn } from 'pages/SignIn';
import { SignOut } from 'pages/SignOut';
import { Tenant } from 'pages/Tenant';
import { TenantAuth } from 'pages/Tenant/TenantAuth';
import { TenantDisplay } from 'pages/Tenant/TenantDisplay';
import { TenantFeatureFlags } from 'pages/Tenant/TenantFeatureFlags';
import { TenantGroups } from 'pages/Tenant/TenantGroups';
import { TenantInformation } from 'pages/Tenant/TenantInformation';
import { TenantInventory } from 'pages/Tenant/TenantInventory';
import { AwsLogo } from 'pages/Tenant/TenantInventory/AwsLogo';
import { TenantLicenses } from 'pages/Tenant/TenantLicenses';
import { TenantIdp } from 'pages/TenantIdp';
import { TenantList } from 'pages/TenantList';
import { SiteConfiguration } from 'components/SiteConfiguration/SiteConfiguration';
import * as React from 'react';
import { Switch } from 'react-router-dom';
import { MobileConfiguration } from 'pages/Tenant/TenantMobile';
import {
  JobExecutionRefreshButton,
  TenantExecutionDetail,
  TenantJobExecutions,
} from 'pages/Tenant/TenantJobExecutions';
import { TenantLogs } from 'pages/Tenant/TenantLogs';
import { Images } from 'pages/Images/Images';
import { ReleaseStages } from 'pages/ReleaseStages';
import { ReleaseStageConfiguration } from 'components/ReleaseStageConfiguration';
import { DdaDefinitionsInformation } from 'pages/Dda';
import { FeatureFlagFleets } from '../pages/FeatureFlag/FeatureFlagFleets';
import { AppRoute, AppRouteProps } from './AppRoute';
import { LearnClients } from '../pages/LearnClients/LearnClients';
import { JobList } from '../components/JobList';
import { TenantRouteProps } from '../pages/Tenant/Tabs/TenantRoutes';
import { TenantSupportLogin } from '../components/TenantSupportLogin';
import { TenantChildren } from '../pages/Tenant/TenantChildren';
import { LearnSites } from 'pages/LearnSites';
import { Fleets } from 'pages/Fleets/Fleets';
import { ArtifactList } from 'pages/ArtifactList';
import { GlobalJobExecutionDetails, GlobalJobExecutions, GlobalJobs } from 'pages/GlobalJobs';
import { Executions } from 'pages/Executions';
import { CohortsUpgrade } from 'pages/Cohorts';
import {
  MassQueryTooling,
  RegionalExecutions,
  RegionalExecutionDetails,
} from 'pages/MassQueryTooling';
import { SharedRds } from 'components/SharedRds/SharedRds';

// See https://reactrouter.com/web/example/route-config
// The order of items corresponds to order of tabs in the UI.

export const externalRoutes: AppRouteProps[] = [
  {
    component: Callback,
    path: '/callback',
    public: true,
  },
  {
    component: ExternalPlaceholder,
    path: '/:tenantId',
  },

  // There are no /sign-in-callback or / (top-level) routes defined here.
  // Instead, <AuthProvider> handles those. See that component for more
  // explanation.
];

function generateRoutes(prefix: string): TenantRouteProps[] {
  // The tenant types are used to determine which routes are available for each tenant type.
  const supportedTenantRouteTypes = [
    'Not Set',
    'Reach',
    'Student',
    'StudentSuccess',
    'Illuminate',
    'LearnDms',
    'Learn',
    'LearnInstance',
    'AFHP',
  ];
  const additionalTenantRoutes: TenantRouteProps[] = [
    {
      component: TenantDisplay,
      exact: true,
      hasTab: true,
      name: 'tenantDisplay.pageTitle',
      tenantTypes: supportedTenantRouteTypes,
      path: `/${prefix}/:tenantId/display`,
    },
    {
      component: TenantGroups,
      hasTab: true,
      name: 'tenantGroups.pageTitle',
      path: `/${prefix}/:tenantId/groups`,
      tenantTypes: supportedTenantRouteTypes,
    },
    {
      component: TenantAuth,
      exact: true,
      hasTab: true,
      name: 'tenantAuth.pageTitle',
      path: `/${prefix}/:tenantId/auth`,
      tenantTypes: supportedTenantRouteTypes,
    },
    {
      component: TenantLicenses,
      hasTab: true,
      name: 'tenantLicenses.pageTitle',
      path: `/${prefix}/:tenantId/licenses`,
      tenantTypes: supportedTenantRouteTypes,
    },
    {
      component: TenantFeatureFlags,
      hasTab: true,
      name: 'tenantFeatureFlags.pageTitle',
      path: `/${prefix}/:tenantId/flags`,
      tenantTypes: [
        'Not Set',
        'Reach',
        'Student',
        'Illuminate',
        'LearnDms',
        'Learn',
        'LearnInstance',
        'Global',
        'StudentSuccess',
        'AFHP',
      ],
      authorization: [
        {
          service: 'feature-flags',
          permission: 'access',
        },
      ],
    },
  ];

  return [
    {
      component: TenantInformation,
      exact: true,
      name: 'tenantProfile.pageTitle',
      path: `/${prefix}/:tenantId`,
      headerActionComponent: TenantSupportLogin,
    },
    {
      component: TenantInformation,
      exact: true,
      hasTab: true,
      name: 'tenantInformation.information',
      path: `/${prefix}/:tenantId/information`,
      headerActionComponent: TenantSupportLogin,
    },
    {
      component: TenantChildren,
      exact: true,
      hasTab: true,
      name: 'tenantAssets.pageTitle',
      path: `/${prefix}/:tenantId/assets`,
      tenantTypes: [
        'Not Set',
        'Reach',
        'Student',
        'StudentSuccess',
        'Illuminate',
        'LearnDms',
        'Customer',
        'ReleaseStage',
        'Learn',
        'LearnInstance',
        'Global',
        'AFHP',
      ],
      condition: (tenant, { learnInstance }) => {
        if (tenant.tenantType === 'Learn') {
          return (learnInstance?.tenantChildren?.length ?? 0) > 0;
        }
        return true;
      },
    },
    ...(prefix === 'tenants' ? additionalTenantRoutes : []),
    ...(prefix === 'learn-site'
      ? additionalTenantRoutes.filter(
          (route) => route.component === TenantLicenses || route.component === TenantFeatureFlags,
        )
      : []),
    {
      component: TenantInventory,
      hasTab: true,
      name: 'tenantInventory.pageTitle',
      path: `/${prefix}/:tenantId/inventory`,
      icon: <AwsLogo />,
      authorization: [
        {
          service: 'blackboard.sites',
          permission: 'view',
        },
      ],
      tenantTypes: ['Learn', 'LearnInstance'],
    },
    {
      component: ReleaseStageConfiguration,
      hasTab: true,
      name: 'releaseStages.pageTitle',
      path: `/${prefix}/:tenantId/release-stage`,
      authorization: [
        {
          service: 'il4',
          permission: 'read',
        },
      ],
      tenantTypes: ['Customer', 'Learn', 'LearnInstance', 'Global'],
    },
    {
      component: SiteConfiguration,
      hasTab: true,
      name: 'siteConfiguration.pageTitle',
      path: `/${prefix}/:tenantId/configuration`,
      authorization: [
        {
          service: 'il4',
          permission: 'read',
        },
      ],
      tenantTypes: ['Customer', 'Learn', 'LearnInstance', 'Global', 'Fleet', 'Illuminate'],
    },
    {
      component: JobList,
      hasTab: true,
      name: 'jobService.pageTitle',
      path: `/${prefix}/:tenantId/jobs`,
      authorization: [
        {
          service: 'il4',
          permission: 'read',
        },
      ],
      tenantTypes: [
        'Not Set',
        'Customer',
        'Learn',
        'LearnInstance',
        'StudentSuccess',
        'ReleaseStage',
        'Illuminate',
        'LearnDms',
        'Global',
        'Fleet',
      ],
    },
    {
      component: TenantJobExecutions,
      hasTab: true,
      exact: true,
      name: 'jobService.executionPageTitle',
      path: `/${prefix}/:tenantId/executions`,
      headerActionComponent: JobExecutionRefreshButton,
      authorization: [
        {
          service: 'il4',
          permission: 'read',
        },
      ],
      tenantTypes: [
        'Not Set',
        'Customer',
        'Learn',
        'LearnInstance',
        'StudentSuccess',
        'ReleaseStage',
        'Illuminate',
        'LearnDms',
        'Global',
        'Fleet',
      ],
    },
    {
      component: TenantExecutionDetail,
      hasTab: false,
      name: 'jobService.executionDetail',
      path: `/${prefix}/:tenantId/executions/:executionId`,
      headerActionComponent: JobExecutionRefreshButton,
      authorization: [
        {
          service: 'il4',
          permission: 'read',
        },
      ],
    },
    {
      component: MobileConfiguration,
      hasTab: true,
      name: 'Mobile',
      path: `/${prefix}/:tenantId/mobileconfiguration`,
      authorization: [
        {
          service: 'mobile',
          permission: 'access',
        },
      ],
      tenantTypes: [
        'Not Set',
        'Reach',
        'Student',
        'StudentSuccess',
        'Illuminate',
        'LearnDms',
        'Learn',
        'LearnInstance',
        'Customer',
        'ReleaseStage',
      ],
    },
    {
      component: TenantLogs,
      hasTab: true,
      name: 'tenantAuditLogs.title',
      path: `/${prefix}/:tenantId/logs`,
      authorization: [
        {
          service: 'il4',
          permission: 'read',
        },
      ],
      tenantTypes: ['Customer', 'Learn', 'LearnInstance', 'Global', 'Fleet'],
    },
    {
      component: DdaDefinitionsInformation,
      exact: true,
      hasTab: true,
      name: 'ddaInformation.pageTitle',
      path: `/${prefix}/:tenantId/dda`,
      authorization: [
        {
          service: 'dda',
          permission: 'access',
        },
      ],
    },
    {
      component: SharedRds,
      hasTab: true,
      name: 'sharedRds.title',
      path: `/${prefix}/:tenantId/shared-rds`,
      authorization: [
        {
          service: 'blackboard.fleets',
          permission: 'view',
        },
      ],
      tenantTypes: ['Fleet'],
    },
  ];
}

const tenantChildRoutes = generateRoutes('tenants');
const clientChildRoutes = generateRoutes('client');
const learnSiteRoutes = generateRoutes('learn-site');
const fleetRoutes = generateRoutes('fleets');

export const internalRoutes: AppRouteProps[] = [
  {
    component: CohortsUpgrade,
    exact: true,
    hasTab: true,
    name: 'cohorts.title',
    path: '/cohorts',
    authorization: [
      {
        service: 'cohort',
        permission: 'search',
      },
    ],
  },
  {
    component: LearnSites,
    exact: true,
    hasTab: true,
    name: 'learnSites.pageTitle',
    path: '/learn-site',
    authorization: [
      {
        service: 'blackboard.sites',
        permission: 'view',
      },
    ],
  },
  {
    component: Tenant,
    name: 'learnSites.pageTitle',
    path: '/learn-site/:tenantId',
    authorization: [
      {
        service: 'blackboard.sites',
        permission: 'view',
      },
    ],
    routes: learnSiteRoutes,
  },
  {
    component: Home,
    exact: true,
    path: '/',
  },
  {
    component: Callback,
    path: '/callback',
    public: true,
  },
  {
    component: SignIn,
    path: '/sign-in',
    public: true,
  },
  {
    component: SignOut,
    path: '/sign-out',
    public: true,
  },
  {
    component: TenantList,
    exact: true,
    hasTab: true,
    name: 'tenantList.pageTitle',
    path: '/tenants',
    authorization: [
      {
        service: 'tenant',
        permission: 'read',
      },
    ],
  },
  {
    component: MassQueryTooling,
    exact: true,
    hasTab: true,
    name: 'massQueryTooling.tabTitle',
    path: '/query-tooling',
    authorization: [
      {
        service: 'query-tooling',
        permission: 'access',
      },
    ],
  },
  {
    component: RegionalExecutions,
    exact: true,
    name: 'massQueryTooling.executions.pageTitle',
    path: '/query-tooling/executions',
    authorization: [
      {
        service: 'query-tooling',
        permission: 'access',
      },
    ],
  },
  {
    component: RegionalExecutionDetails,
    name: 'jobService.executionDetail',
    path: '/query-tooling/regions/:region/executions/:executionId',
    authorization: [
      {
        service: 'query-tooling',
        permission: 'access',
      },
    ],
  },
  {
    component: LicenseList,
    hasTab: true,
    name: 'licenseList.pageTitle',
    path: '/licenses',
    authorization: [
      {
        service: 'license',
        permission: 'read',
      },
    ],
  },
  {
    component: GroupList,
    hasTab: true,
    name: 'groupList.pageTitle',
    path: '/groups',
    authorization: [
      {
        service: 'fnds-group',
        permission: 'read',
      },
    ],
  },
  {
    component: FeatureFlagList,
    exact: true,
    hasTab: true,
    name: 'featureFlagList.pageTitle',
    path: '/feature-flags',
    authorization: [
      {
        service: 'feature-flags',
        permission: 'access',
      },
    ],
  },
  {
    component: ReleaseStages,
    hasTab: true,
    name: 'releaseStages.pageTitle',
    path: '/release-stage',
    exact: true,
    authorization: [
      {
        service: 'il4',
        permission: 'read',
      },
    ],
  },
  {
    component: LearnClients,
    exact: true,
    hasTab: true,
    name: 'learnClients.pageTitle',
    path: '/client',
    authorization: [
      {
        service: 'blackboard.management',
        permission: 'view',
      },
    ],
  },
  {
    component: ArtifactList,
    exact: true,
    hasTab: true,
    name: 'artifactLists.pageTitle',
    path: '/artifacts',
    authorization: [
      {
        service: 'blackboard.artifacts',
        permission: 'view',
      },
    ],
  },
  {
    component: Fleets,
    exact: true,
    hasTab: true,
    name: 'fleetService.pageTitle',
    path: '/fleets',
    authorization: [
      {
        service: 'blackboard.fleets',
        permission: 'view',
      },
    ],
  },
  {
    component: Tenant,
    name: 'fleetService.pageTitle',
    path: '/fleets/:tenantId',
    authorization: [
      {
        service: 'blackboard.fleets',
        permission: 'view',
      },
    ],
    routes: fleetRoutes,
  },
  {
    component: Images,
    hasTab: true,
    name: 'images.pageTitle',
    path: `/registry`,
    exact: true,
    authorization: [
      {
        service: 'il4',
        permission: 'read',
      },
    ],
  },
  {
    component: GlobalJobs,
    hasTab: true,
    name: 'jobService.pageTitle',
    path: `/jobs`,
    exact: true,
    authorization: [
      {
        service: 'il4',
        permission: 'read',
      },
    ],
  },
  {
    component: Executions,
    hasTab: true,
    name: 'executions.pageTitle',
    path: `/executions`,
    exact: true,
    // Until the lct-jobs region execution list (lct-jobs#437) is live in every stage
    devOnly: true,
    authorization: [
      {
        service: 'il4',
        permission: 'read',
      },
    ],
  },
  {
    component: GlobalJobExecutions,
    name: 'jobService.executionPageTitle',
    path: `/jobs/executions`,
    exact: true,
    authorization: [
      {
        service: 'il4',
        permission: 'read',
      },
    ],
  },
  {
    component: GlobalJobExecutionDetails,
    hasTab: false,
    name: 'jobService.executionDetail',
    path: `/jobs/executions/:executionId`,
    authorization: [
      {
        service: 'il4',
        permission: 'read',
      },
    ],
  },
  {
    component: Tenant,
    path: '/client/:tenantId',
    name: 'learnClient.pageTitle',
    authorization: [
      {
        service: 'tenant',
        permission: 'read',
      },
    ],
    routes: clientChildRoutes,
  },
  {
    component: TenantIdp,
    exact: true,
    name: 'tenantIdp.pageTitle',
    path: '/tenants/:tenantId/auth/:idpId',
    authorization: [
      {
        service: 'identity-provider',
        permission: 'read',
      },
      {
        service: 'tenant',
        permission: 'read',
      },
    ],
  },
  {
    component: Tenant,
    path: '/tenants/:tenantId',
    authorization: [
      {
        service: 'tenant',
        permission: 'read',
      },
    ],
    routes: tenantChildRoutes,
  },
  {
    component: FeatureFlagDefinition,
    path: '/feature-flags/:flagKey',
    authorization: [
      {
        service: 'feature-flags',
        permission: 'access',
      },
    ],
    routes: [
      {
        component: FeatureFlagDefinitionInformation,
        exact: true,
        name: 'featureFlagDefinitionInfo.globalSettingsTitle',
        path: '/feature-flags/:flagKey',
      },
      {
        component: FeatureFlagDefinitionInformation,
        exact: true,
        hasTab: true,
        name: 'featureFlagDefinitionInfo.globalSettingsTitle',
        path: '/feature-flags/:flagKey/information',
      },
      {
        component: FeatureFlagTenants,
        exact: true,
        hasTab: true,
        name: 'featureFlagDefinitionTenants.pageTitle',
        path: '/feature-flags/:flagKey/tenants',
      },
      {
        component: FeatureFlagRegions,
        exact: true,
        hasTab: true,
        name: 'featureFlagDefinitionRegions.regionsLabel',
        path: '/feature-flags/:flagKey/regions',
      },
      {
        component: FeatureFlagFleets,
        exact: true,
        hasTab: true,
        name: 'featureFlagDefinitionFleets.fleetsLabel',
        path: '/feature-flags/:flagKey/fleets',
      },
    ],
  },
  {
    component: ErrorPage,
    path: '*',
  },
];

const filterRoutesByEnvironment = (routes: AppRouteProps[], stage: string): AppRouteProps[] => {
  const isMockData =
    process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_API === 'mock';
  const isProd = stage !== 'local' && stage !== 'dev' && stage !== 'int' && stage !== 'tp';

  return routes
    .filter((route) => {
      if (route.mockDataOnly && !isMockData) {
        return false;
      }

      if (route.devOnly && isProd) {
        return false;
      }

      return true;
    })
    .map((route) => {
      if (route.routes) {
        return {
          ...route,
          routes: filterRoutesByEnvironment(route.routes, stage),
        };
      }
      return route;
    });
};

export const Routes: React.FC = () => {
  const { loading: configLoading, deployment, stage } = useAppConfigContext();

  if (configLoading || !deployment) {
    return null;
  }

  const routesToUse = filterRoutesByEnvironment(
    deployment.internal ? [...internalRoutes] : [...externalRoutes],
    stage || 'dev',
  );

  return (
    <AppRouteContextProvider routes={routesToUse}>
      <Switch>
        {routesToUse.map((route, i) => (
          <AppRoute key={i} {...route} />
        ))}
      </Switch>
    </AppRouteContextProvider>
  );
};
